// ============================================================================
// Report Operativi — persistenza reale FastAPI + MongoDB (Module E).
// ----------------------------------------------------------------------------
// La GENERAZIONE resta un MOCK client-side (generateOperationalReport): questo
// store si occupa solo di PERSISTERE ciò che il generatore produce. Cache per
// clientId idratata on-demand; se il backend è vuoto per un cliente al primo
// accesso, i report di esempio vengono generati client-side e persistiti.
// ============================================================================
import { api } from "@/lib/api";
import { generateOperationalReport } from "@/lib/operationalReport";

const _byClient = {};        // { clientId: [report,...] }
const _hydrated = new Set();
const _subs = new Set();
const notify = () => _subs.forEach((fn) => { try { fn(); } catch { /* noop */ } });
export const subscribeReports = (fn) => { _subs.add(fn); return () => _subs.delete(fn); };
export const resetReports = () => { Object.keys(_byClient).forEach((k) => delete _byClient[k]); _hydrated.clear(); };

const sortReports = (list) =>
  [...list].sort((a, b) => (b.month_key || "").localeCompare(a.month_key || "") || (b.created_at || "").localeCompare(a.created_at || ""));

const toApi = (r) => ({
  client_id: r.clientId, month: r.month, month_key: r.monthKey, status: r.status,
  generated_by: r.generatedBy, generated_by_id: r.generatedById, notes: r.notes,
  data: r.data, prompt: r.prompt, data_snapshot: r.data, updated_by: r.updatedBy,
  created_at: r.createdAt, last_updated: r.lastUpdated,
});
const fromApi = (d) => ({
  id: d.id, clientId: d.client_id, month: d.month, monthKey: d.month_key, status: d.status,
  generatedBy: d.generated_by, generatedById: d.generated_by_id, notes: d.notes,
  data: d.data, prompt: d.prompt, createdAt: d.created_at, lastUpdated: d.last_updated, updatedBy: d.updated_by,
});

const seedFor = (client) => {
  const mk = (mLabel, mKey, by, date, notes) => {
    const { data, prompt } = generateOperationalReport({ client, days: 30, period: mLabel, notes, author: by });
    return { id: `${client.id}_seed_${mKey}`, clientId: client.id, month: mLabel, monthKey: mKey, status: "generated",
      generatedBy: by, generatedById: null, notes, data, prompt, createdAt: date, lastUpdated: date, updatedBy: by };
  };
  return [
    mk("Maggio 2026", "2026-05", "Beatrice Conti", "2026-05-31T18:20:00.000Z", "Nuove creatività video e ottimizzazione del budget di retargeting."),
    mk("Aprile 2026", "2026-04", "Antonella Greco", "2026-04-30T17:05:00.000Z", "Nuova offerta di lancio e landing page aggiornata."),
  ];
};

async function hydrateReports(client) {
  const cid = client.id;
  try {
    const { data } = await api.get("/reports", { params: { client_id: cid } });
    let list = (Array.isArray(data) ? data : []).map(fromApi);
    if (list.length === 0) {
      const seeds = seedFor(client);
      await Promise.all(seeds.map((r) => api.put(`/reports/${r.id}`, toApi(r)).catch(() => {})));
      list = seeds;
    }
    _byClient[cid] = list;
  } catch {
    _byClient[cid] = _byClient[cid] || [];
  } finally {
    _hydrated.add(cid);
    notify();
  }
}

export const getReports = (client) => {
  const cid = client.id;
  if (!_hydrated.has(cid)) { if (!(cid in _byClient)) _byClient[cid] = []; _hydrated.add(cid); hydrateReports(client); }
  return sortReports(_byClient[cid] || []);
};

export const upsertReport = (clientId, report) => {
  const list = _byClient[clientId] || [];
  const idx = list.findIndex((r) => r.id === report.id);
  if (idx >= 0) list[idx] = report; else list.unshift(report);
  _byClient[clientId] = [...list];
  notify();
  api.put(`/reports/${report.id}`, toApi(report)).catch(() => hydrateReports({ id: clientId }));
};

export const removeReport = (clientId, id) => {
  _byClient[clientId] = (_byClient[clientId] || []).filter((r) => r.id !== id);
  notify();
  api.delete(`/reports/${id}`).catch(() => hydrateReports({ id: clientId }));
};
